<template>
  <section class="calc-section">
    <TitleSection>
      {{ _T("@Calculation of battery capacity") }}
    </TitleSection>
    <section class="calc-section__body">
      <div class="calc-section__form-without-checkboxes">
        <section class="calc-section__inputs-wrapper">
          <section class="calc-section__inputs-enter">
            <div class="calc-section__data-input-section">
              <div class="calc-section__input-help">
                <h3 class="calc-section__subtitle source-power">
                  {{ _T("@Load W") }}
                </h3>
                <ButtonHelper :gray-color="true">
                  {{ helperTest.UPSpower?.[getUserLanguage.name] }}
                </ButtonHelper>
              </div>

              <InputPower
                v-model.number="calculationBattery.powerUPS"
                :error-state="stateInput.powerUPS"
                :type-input="'number'"
                :max-size="6"
                :input-id="'w'"
              >
                W
              </InputPower>
            </div>

            <div class="calc-section__data-input-section">
              <div class="calc-section__input-help switch">
                <h3 class="calc-section__subtitle source-power">
                  {{ _T("@Inverter efficiency") }}:
                </h3>
                <ButtonHelper :gray-color="true">
                  {{ helperTest.inverterEfficiency?.[getUserLanguage.name] }}
                </ButtonHelper>
                <InputSwitch v-model="stateSwitch.switchBatteryCapacity" />
              </div>

              <InputPower
                v-model.number="calculationBattery.inverterEfficiency"
                :error-state="stateInput.inverterEfficiency"
                :type-input="'number'"
                :max-size="6"
                :input-id="'w'"
                :is-disable="!stateSwitch.switchBatteryCapacity"
              >
                %
              </InputPower>
            </div>

            <div class="calc-section__data-input-section">
              <div class="calc-section__input-help">
                <h3 class="calc-section__subtitle source-power">
                  {{ _T("@Rated battery voltage") }}:
                </h3>
                <ButtonHelper :gray-color="true">
                  {{ helperTest.ratedBatteryVoltage?.[getUserLanguage.name] }}
                </ButtonHelper>
              </div>

              <InputPower
                v-model.number="calculationBattery.ratedBatteryVoltage"
                :error-state="stateInput.ratedBatteryVoltage"
                :type-input="'number'"
                :max-size="6"
                :input-id="'v'"
              >
                V
              </InputPower>
            </div>

            <div class="calc-section__data-input-section">
              <div class="calc-section__input-help">
                <h3 class="calc-section__subtitle source-power">
                  {{ _T("@Working time") }}:
                </h3>
                <ButtonHelper :gray-color="true">
                  {{ helperTest.workingTime?.[getUserLanguage.name] }}
                </ButtonHelper>
              </div>

              <InputPower
                v-model.number="calculationBattery.time"
                :error-state="stateInput.time"
                :type-input="'number'"
                :max-size="5"
                :input-id="'time'"
              >
                {{ _T("@Hours") }}
              </InputPower>
            </div>
          </section>

          <ButtonPrimary button-width="363px" @click.prevent="calcBattery">
            {{ _T("@Calculate") }}
          </ButtonPrimary>
        </section>

        <div class="calc-section__subtitle source-power result">
          <TitleSection>{{ _T("@Calculation result") }}:</TitleSection>
          <p>
            {{ _T("@Minimum battery capacity") }} =
            <span v-show="calcBatteryResult.length"
              >{{ calcBatteryResult }} Ah</span
            >
          </p>
        </div>
      </div>
    </section>

    <div class="calc-section__slider">
      <TitleSection>{{ _T("@Popular") }}</TitleSection>
      <ProductCarouselByLabel label="bestSeller" />
    </div>
  </section>
</template>

<script setup>
import ButtonHelper from "~~/modules/shared/buttons/ButtonHelper.vue";
import TitleSection from "~/modules/calculators/batteryCalculator/components/sections/TitleSection.vue";
import InputPower from "~/modules/calculators/batteryCalculator/components/UI/InputPower.vue";
import ButtonPrimary from "~/modules/shared/buttons/ButtonPrimary.vue";
import InputSwitch from "~~/modules/shared/inputs/InputSwitch.vue";
import { useLanguageStore } from "~/store/languageStore";
import ProductCarouselByLabel from "~/modules/shared/sliders/productCarousels/ProductCarouselByLabel.vue";

const languageStore = useLanguageStore();
const { getUserLanguage } = storeToRefs(languageStore);

const calcBatteryResult = ref("");

const helperTest = reactive({
  UPSpower: {
    ru: "Суммарная нагрузка, которая подается на ИБП. Можно посмотреть в тех документации приборов, которые подключены к ИБП",
    uk: "Сумарне навантаження, яка подається на ДБЖ. Можна подивитися в тих документації приладів, які підключені до ДБЖ",
  },
  inverterEfficiency: {
    ru: "КПД инвертора можно посмотреть в паспорте ИБП, если вы не знаете это значение, то ставьте по умолчанию 0,8.",
    uk: "ККД інвертора можна подивитися в паспорті ДБЖ, якщо ви не знаєте це значення, то ставте за замовчуванням 0,8.",
  },
  workingTime: {
    ru: "Время которое должен проработать ИБП",
    uk: "Час яке повинен працювати ДБЖ",
  },
  ratedBatteryVoltage: {
    ru: "Номинальное напряжение аккумуляторной батареи.",
    uk: "Номінальна напруга акумуляторної батареї.",
  },
});

const calculationBattery = reactive({
  powerUPS: "",
  inverterEfficiency: 0.8,
  time: "",
  ratedBatteryVoltage: "",
});

const stateSwitch = reactive({
  switchBatteryCapacity: false,
  switchBackupTime: false,
});

const stateInput = reactive({
  powerUPS: false,
  inverterEfficiency: false,
  time: false,
  ratedBatteryVoltage: false,
});

function calcBattery() {
  let a;
  let Ah;
  let total;
  let resultState = false;

  for (const key in calculationBattery) {
    if (calculationBattery[key] === "") {
      stateInput[key] = true;
      resultState = false;
    } else {
      stateInput[key] = false;
      resultState = true;
    }
  }

  if (resultState) {
    a = calculationBattery.powerUPS / calculationBattery.ratedBatteryVoltage;
    Ah = a * calculationBattery.time;
    if (calculationBattery.inverterEfficiency > 1) {
      total = +Ah / (calculationBattery.inverterEfficiency / 100);
    } else {
      total = +Ah / calculationBattery.inverterEfficiency;
    }
    calcBatteryResult.value = total.toFixed(0);
  }
}
</script>

<style lang="scss" scoped>
.calc-section {
  @include flex-container(column, center);

  gap: 48px;

  &__inputs-wrapper {
    @include flex-container(column, flex-start);
    gap: 56px;

    margin-bottom: 56px;

    @include bigMobile {
      margin-bottom: 48px;
    }
  }

  &__form-without-checkboxes {
    margin-bottom: 56px;

    @include bigMobile {
      margin-bottom: 48px;
    }
  }

  &__checkbox {
    @include flex-container(row, null, center);
    column-gap: 16px;
  }

  &__data-input-section {
    max-width: 327px;
    width: 100%;

    @include bigMobile {
      max-width: 100%;
    }
  }

  &__subtitle {
    @include font(18, 28, 400);
    letter-spacing: 0.02em;
    color: #2b2b2b;

    &.source-power {
      margin: 0;

      @media (max-width: 1250px) {
        max-width: 330px;
      }

      @include bigMobile {
        @include font(16, 22, 400);
      }

      &.result {
        @include flex-container(column, center, flex-start);
        gap: 8px;
      }
    }
  }

  &__input-help {
    @include flex-container(row, null, center);
    gap: 16px;

    margin-bottom: 24px;

    &.switch {
      margin-bottom: 22px;
    }
  }

  &__inputs-enter {
    width: 100%;

    @include flex-container(row, flex-start, baseline);
    gap: 24px;
    flex-wrap: wrap;

    @include bigMobile {
      flex-direction: column;
      gap: 32px;
    }
  }

  &__slider {
    @extend %width-main;

    @include flex-container(column, center, center);
    gap: 16px;
  }
}
</style>
